import {
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
  map,
  BehaviorSubject,
  Subscription,
  tap,
  filter,
  merge,
  startWith
} from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import {
  FailureCodeModel,
  FailureDescription,
  FailureDialogData,
  FormEntry,
  getFormEntryControlName,
  getFormEntryInstanceKey
} from '../../../go/model/form.model';
import { Task } from 'src/app/modules/go/model/task.model';
import {
  FailureRegisterDialogComponent
} from '../failure-register-dialog/failure-register-dialog.component';
import { getTaskGps } from '../../../go/utils/task.utils';
import { DialogService } from '../../../../services/dialog.service';
import { BaseControlModel } from '../../../../model/general.model';
import { getFailureHeaders } from '../../../go/utils/form.utils';
import { FailureService } from "../../../../services/failure.service";
import { StoreService } from "../../../../services/store.service";
import { FailureListDialogComponent } from "../../../go/components/_dialog/failure-list-dialog/failure-list-dialog.component";

const FAILURE_DESCRIPTION = 'failureDescription'
const NOTES_ENTRY_NAME = 'uwagi'
@Component({
  selector: 'dynamic-form-item',
  templateUrl: './dynamic-form-item.component.html',
  styleUrls: ['./dynamic-form-item.component.scss']
})
export class DynamicFormItemComponent implements OnInit, OnChanges, OnDestroy {

  @Input() form: FormGroup;
  @Input() item: FormEntry;
  @Input() parentEntry: FormEntry;
  @Input() defectsNo: number;
  @Input() indent = 0;
  @Input() readonly = false;
  @Input() task: Task;
  @Input() previewMode = false;
  @Input() failureId?: number;
  @Input() focusEntryKey?: string;
  @Input() validationRefreshTick = 0;
  @Input() blocked = false;
  controlIdWithFailure: number;

  // Główna pozycja "Uwagi" — rozpoznawana po nazwie, tak samo jak w backendzie
  get isNotesEntry(): boolean {
    return this.indent === 0 && this.item.name?.toLowerCase() === NOTES_ENTRY_NAME;
  }

  get entryInstanceKey(): string {
    return getFormEntryInstanceKey(this.item.templateFormEntryId, this.item.cloneIndex);
  }

  get entryElementId(): string {
    return `form-entry-${this.entryInstanceKey.replace(':', '-')}`;
  }

  group: FormGroup;
  failureDescription: FormControl<FailureDescription[]>;
  private failureWatcherSub?: Subscription;

  isFailureValue$ = new BehaviorSubject<boolean>(false);
  subs = new Subscription();
  failureCodesFromOption: FailureCodeModel[] = [];

  hasDefects(): boolean {
    return this.failureDescription.value && this.failureDescription.value.length > 0;
  }

  hasNoDefects(): boolean {
    return this.failureDescription.value && this.failureDescription.value.length === 0;
  }

  constructor(
    private _dialog: MatDialog,
    private dialogService: DialogService,
    private failureService: FailureService,
    private store: StoreService,
    private cdr: ChangeDetectorRef
  ){
  }

  ngOnInit(): void {
    if(this.item.children || this.item.control){
      this.group = new FormGroup({});
      this.form.addControl(
        this.previewMode
          ? `_${this.item.id}`
          : getFormEntryControlName(this.item.templateFormEntryId, this.item.cloneIndex),
        this.group
      )
    }
    else {
      this.group = this.form;
    }
    this.failureDescription = new FormControl<any>(this.item.failureDescription);
    this.group.addControl(FAILURE_DESCRIPTION, this.failureDescription);
    this.applyReadonlyState();

    setTimeout(() => this.setupFailureWatchers(), 0);

    this.checkIfShowFailureDialog();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['readonly'] && this.failureDescription) {
      this.applyReadonlyState();
    }

    if (changes['validationRefreshTick'] && !changes['validationRefreshTick'].firstChange) {
      this.cdr.markForCheck();
    }
  }

  private checkIfShowFailureDialog(): void {
    if (!this.failureId) {
      return;
    }

    if (this.item.failureDescription) {
      const index = this.item.failureDescription?.findIndex(x => x.id === this.failureId);
      if (index >= 0) {
        this.editFailure(index, true);
      }
    }
  }

  scrollToElement(elemId: string) {
    console.log('scroll', elemId)
    const el = document.getElementById(elemId);
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 100;

      window.scrollTo({
        top,
        behavior: 'smooth'
      });    }
  }

  editFailure(index: number, withScroll = false) {
    const failureDescription = this.failureDescription.value[index];
    const dialogRef = this._dialog.open<FailureRegisterDialogComponent, FailureDialogData, FailureDescription>(FailureRegisterDialogComponent, {
      data: {
        failureDescription,
        failureCodeList: this.getCodes(),
        disabled: this.readonly || failureDescription.status === 'COMPLETED' || failureDescription.status === 'IN_PROGRESS',
        failureMultiselect: this.item.failureMultiselect,
        defectsLastNo: this.defectsNo,
        taskName: this.task.name,
        taskVersion: this.task.templateImplementationVersion,
        taskId: this.task.id,
        headers: getFailureHeaders(this.task),
        longitude: failureDescription.longitude,
        latitude: failureDescription.latitude,
        entry: this.item,
        parentEntry: this.parentEntry,
        ...this.getCloneData()
      },
      panelClass: 'dialog-mobile'
    });
    this.subs.add(
      dialogRef.afterClosed().pipe(
      tap(() => {
        if (withScroll) {
          this.scrollToElement('failure-' + failureDescription.id);
        }
      }),
      filter(value => !!value),
      tap(value => this.failureDescription.setValue([...this.failureDescription.value ?? []].map((failure, idx) => idx === index ? value! : failure))),
    ).subscribe());

    this.subs.add(dialogRef.componentInstance.deleteDefectEvent.subscribe(() => this.removeFailure(index)));
  }

  registerFailure() {
    const dialogRef = this._dialog.open<FailureRegisterDialogComponent, FailureDialogData, FailureDescription>(FailureRegisterDialogComponent, {
      data: {
        failureCodeList: this.getCodes(),
        failureMultiselect: this.item.failureMultiselect,
        defectsLastNo: this.defectsNo,
        taskName: this.task.name,
        taskVersion: this.task.templateImplementationVersion,
        taskId: this.task.id,
        headers: getFailureHeaders(this.task),
        entry: this.item,
        parentEntry: this.parentEntry,
        disabled: false,
        ...getTaskGps(this.task),
        ...this.getCloneData()
      },
      panelClass: 'dialog-mobile'
    });
    this.subs.add(dialogRef.afterClosed().pipe(
      filter(value => !!value),
      tap(value => this.failureDescription.setValue([...this.failureDescription.value ?? [], value!])),
    ).subscribe());
  }

  private getCloneData(): Pick<FailureDialogData, 'cloneTaskName' | 'customLabel' | 'customValue'> {
    return {
      cloneTaskName: this.item.cloneTaskName,
      customLabel: this.item.customLabel,
      customValue: this.item.customValue
    };
  }

  removeFailure(index: number) {
    this.failureDescription.setValue([...this.failureDescription.value ?? []].filter((_, idx) => idx !== index))
  }

  private checkIfFailure(currentValue: Record<string, any>): boolean {
    if (!this.item.control) return false;

    return this.item.control.some(control => {
      const controlId = this.previewMode ? control.id : control.templateFormControlId;
      const controlValue = currentValue[controlId];

      let isDefect;

      if (control.type === 'MULTISELECT' && controlValue) {
        const failureOptions = control.options?.filter(option => option.isFailure).map(option => option.value) ?? [];
        isDefect = (controlValue as string[]).some(value => failureOptions.includes(value));

        if (isDefect) {
          const selectedOptions = control.options?.filter(option => option.isFailure && controlValue.includes(option.value));
          if (selectedOptions?.length) {
            this.failureCodesFromOption = [];
            selectedOptions.forEach(option => option.failureCode && this.failureCodesFromOption.push(option.failureCode))
          }
        }
      } else {
        isDefect = control.options?.some(option => option.value === controlValue && option.isFailure) || false;
        if (isDefect) {
          const selectedOption = control.options?.find((option) => option.value === controlValue && option.isFailure);

          if (selectedOption && selectedOption.failureCode) {
            this.failureCodesFromOption = [];
            this.failureCodesFromOption.push(selectedOption.failureCode)
          }
        }
      }

      if (isDefect) {
        this.failureDescription.setValidators(Validators.required);
        this.controlIdWithFailure = controlId;

      } else {
        this.failureDescription.setValidators(null);
      }

      if (this.readonly) {
        this.failureDescription.setValidators(null);
      }

      this.failureDescription.updateValueAndValidity({emitEvent: false})

      return isDefect;
    });
  }

  valueChanged(event: BaseControlModel) {

    if (!this.controlIdWithFailure || !this.isFailureValue$.value || event.id !== this.controlIdWithFailure.toString()) {
      return;
    }

    if (this.failureDescription.value.some(failure => (failure.status === 'IN_PROGRESS' || failure.status === 'COMPLETED'))) {
      this.subs.add(this.dialogService.promptForIrremovableFailure().afterClosed().subscribe(result => {
        this.group.controls[event.id].setValue(event.previousValue);
      }));
      return;
    }


    if (this.failureDescription.value?.length) {
      this.subs.add(this.dialogService.promptForFailureOptionChange().afterClosed().subscribe(result => {
        if (result) {
          this.failureDescription.setValue([]);
          this.clearControlValues();
        } else {
          this.group.controls[event.id].setValue(event.previousValue);
        }
      }));
    } else {
      this.clearControlValues();
    }
  }

  private clearControlValues(): void {
    Object.keys(this.group.controls).forEach(controlKey => {
      if (parseInt(controlKey) !== this.controlIdWithFailure && controlKey !== FAILURE_DESCRIPTION) {
        this.group.controls[controlKey]?.setValue(null);
      }
    });
  }

  private getCodes(): FailureCodeModel[] {

    if (this.failureCodesFromOption.length) {
      return this.failureCodesFromOption;
    }

    return this.item.failureCodeList || [];
  }

  showFailuresHistory() {
    const fromDate = this.task.historicalFailuresFrom ?? this.prepareFromDate(this.task.createdOn);
    const toDate = this.task.historicalFailuresTo ?? this.task.createdOn.split('T')[0];
    if (this.item.failureCodeList) {
      const codes = this.item.failureCodeList.map(item => item.code);
      const { tower, span } = this.task;
      this.store.showLoader();
      if (tower) {
        this.subs.add(this.failureService.getTowerFailures(tower.latitude, tower.longitude, codes, fromDate, toDate, this.task.id).pipe(
          tap(data => this.handleFailureHistoryResponse(data))
        ).subscribe())
      }

      if (span) {
        const fallbackLocation = getTaskGps(this.task);
        this.subs.add(this.failureService.getSpanFailures(span.latitude ?? fallbackLocation.latitude, span.longitude ?? fallbackLocation.longitude, codes, fromDate, toDate, this.task.id).pipe(
          tap(data => this.handleFailureHistoryResponse(data))
        ).subscribe())
      }
    }

  }

  private prepareFromDate(createdOn: string): string {
    const date = new Date(createdOn);
    return new Date(date.getFullYear() - 5, date.getMonth(), date.getDate()).toISOString().split('T')[0];
  }

  private handleFailureHistoryResponse(data: FailureDescription[]) {
    this.store.hideLoader();

    if (!data.length) {
      this.store.showWarningMsg('EMPTY_FAILURES_HISTORY');
      return;
    }

    this._dialog.open<FailureListDialogComponent, FailureDescription[]>(FailureListDialogComponent, {
      data
    })
  }

  private setupFailureWatchers(): void {
    this.failureWatcherSub?.unsubscribe();

    const controlsToWatch = (this.item.control || [])
      .map(control => {
        const controlId = this.previewMode ? control.id : control.templateFormControlId;
        return this.group.get(String(controlId));
      })
      .filter((ctrl): ctrl is FormControl => !!ctrl);

    if (!controlsToWatch.length) {
      this.isFailureValue$.next(false);
      return;
    }

    this.failureWatcherSub = merge(...controlsToWatch.map(ctrl => ctrl.valueChanges.pipe(startWith(ctrl.value))))
      .pipe(map(() => this.checkIfFailure(this.group.getRawValue())))
      .subscribe(this.isFailureValue$);
    this.subs.add(this.failureWatcherSub);
  }

  trackByControl = (_: number, control: { id: number; templateFormControlId: number }) =>
    this.previewMode ? control.id : control.templateFormControlId;

  trackByEntry = (_: number, entry: FormEntry) =>
    this.previewMode ? entry.id : getFormEntryInstanceKey(entry.templateFormEntryId, entry.cloneIndex);

  trackByFailure = (_: number, item: FailureDescription) => item.id;

  private applyReadonlyState(): void {
    if (this.readonly) {
      this.failureDescription.disable({ emitEvent: false });
    } else {
      this.failureDescription.enable({ emitEvent: false });
    }

    this.cdr.markForCheck();
  }
}
